import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Loader } from '@components/atoms/loader';
import { ToastContainer } from '@components/moleculs/toast-container';
import deviceDetect from '@dataspike/device-detect';
import { useScreenFullHeight } from '@hooks/use-screen-full-height';
import type { VerificationProfileModel, VerificationProfileSettingsModel } from '@models';
import type { DocsVerificationPhotosKeysEnum } from '@models/enums';
import { DocsVerificationStepEnum, LangEnum } from '@models/enums';
import { DocsVerificationSteps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/docs-verification-steps';
import { VerificationError } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/docs-verification-steps/verification-error';
import { DemoDataContextProvider } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/demo-data-provider';
import { WidgetContextProvider } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import { PersonalDataContextProvider } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/provider';
import { VERIFICATION_RESPONSE } from '@modules/docs-verification-widget/demo-responses/verification-response';
import { useDeviceOrientation } from '@modules/docs-verification-widget/hooks/use-device-orientation';
import type { DomainSettingsModel } from '@modules/shared/models/white-label/domain-settings-model';
import { domainSlice } from '@modules/shared/slices/domain-slice';
import Box from '@mui/material/Box';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import type { ThemeOptions } from '@mui/material/styles';
import {
    createTheme,
    StyledEngineProvider,
    ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import type { StoreWrapperProps } from '@root/types';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { themeSlice } from '@store/slices/theme-slice';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import type { DefaultTheme } from 'styled-components';
import { ThemeProvider } from 'styled-components';

import { generateMediaHelpers, theme } from '../../theme';

import { useLazyGetVerificationByIdQuery, useUploadFingerprintSDKMutation } from './api';
import { GlobalStyles } from './global-styles';
import { createLocalization, MUI_LOCALES } from './localization';
import type { RootState } from './store';
import { store } from './store';
import { DocsVerificationWidgetContainer } from './styles';
import type { DocsVerificationWidgetProps } from './types';

setupListeners(store.dispatch);
const i18nInstance = createLocalization();

const convertChecks = (settings: VerificationProfileModel['settings']) =>
    [
        ...(settings.poi_required ? settings.poi_allowed_documents : []),
        ...(settings.face_comparison_required ? settings.face_comparison_allowed_documents : []),
        ...(settings.poa_required ? ['poa'] : []),
    ] as DocsVerificationPhotosKeysEnum[];

const StoreWrapper = ({ children }: StoreWrapperProps) => (
    <Provider store={store}>{children}</Provider>
);

export const Widget = ({
    onInit,
    settings,
    theme: customTheme,
    isDemo,
    defaultLanguage,
    ...rest
}: DocsVerificationWidgetProps) => {
    const { i18n, ready } = useTranslation();
    const externalSettings = settings as DomainSettingsModel;
    const domainSettings = useSelector((state: RootState) => state.domain.settings);
    const currentTheme = useSelector((state: RootState) => state.theme.currentTheme);
    const sdkOptions = useSelector((state: RootState) => state.docsVerification.sdkOptions);
    const currentStep = useSelector((state: RootState) => state.docsVerification.currentStep);
    const dispatch = useDispatch();
    const { isLandscape } = useDeviceOrientation();
    const fingerprintIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [uploadFingerprint] = useUploadFingerprintSDKMutation();
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const lang = searchParams.get('lang')?.toLowerCase();

        let actualLang = '';

        Object.values(LangEnum).forEach((val) => {
            if (val === lang) {
                actualLang = val;
            }
        });

        i18nInstance.changeLanguage(actualLang || defaultLanguage || LangEnum.EN);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const mainTheme = useMemo(
        () =>
            createTheme(
                merge(cloneDeep({ ...(currentTheme as ThemeOptions) }), domainSettings.ui_settings),
                merge(
                    { ...theme },
                    { ...MUI_LOCALES[i18n.language as keyof typeof MUI_LOCALES] },
                    customTheme,
                ),
                generateMediaHelpers(currentTheme),
            ),
        [currentTheme, domainSettings, customTheme, i18n.language],
    ) as unknown as DefaultTheme;

    const [getVerificationById] = useLazyGetVerificationByIdQuery();
    const isInitialized = useRef(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const { height } = useScreenFullHeight();

    useEffect(() => {
        dispatch(themeSlice.actions.setIsLandscape(isLandscape));
    }, [dispatch, isLandscape]);

    useEffect(() => {
        if (externalSettings) {
            dispatch(
                domainSlice.actions.setSettings(merge(cloneDeep(domainSettings), externalSettings)),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;

            const init = async () => {
                try {
                    const verification = isDemo
                        ? VERIFICATION_RESPONSE
                        : await getVerificationById({
                              id: rest.id,
                              url: rest.apiUrl,
                          }).unwrap();

                    if (onInit) {
                        await onInit(verification.status);
                    }

                    const search = new URLSearchParams(window.location.search);
                    const source = search.get('source');

                    dispatch(
                        docsVerificationSlice.actions.useSDK({
                            id: rest.id,
                            elementId: rest.elementId,
                            settings: verification.settings as VerificationProfileSettingsModel,
                            checks: verification.checks
                                ? convertChecks(
                                      verification.settings as VerificationProfileSettingsModel,
                                  )
                                : [],
                            apiUrl: rest.apiUrl,
                            logsUrl: rest.logsUrl,
                            redirectUrl: rest.redirectUrl,
                            status: verification.status,
                            verificationUrl: verification.verification_url,
                            expiresAt: verification.expires_at,
                            source,
                            isDemo: !!isDemo,
                            isWeb3: verification.settings?.is_web3,
                            ...(verification.settings?.is_web3
                                ? {
                                      web3Options: {
                                          wallet: verification.wallet as string,
                                      },
                                  }
                                : {}),
                        }),
                    );
                } catch (e) {
                    if (onInit) {
                        await onInit('error', e);
                    }
                    setIsError(true);
                } finally {
                    setIsLoading(false);
                }
            };

            init();
        }
    }, [getVerificationById, rest, dispatch, onInit, isDemo]);

    useEffect(() => {
        if (sdkOptions?.id && currentStep === DocsVerificationStepEnum.VERIFICATION) {
            const checkDevice = () => {
                deviceDetect
                    .init({
                        checkCamera: true,
                    })
                    .then((response) => {
                        response.get().then((deviceData) => {
                            uploadFingerprint({
                                url: sdkOptions.apiUrl,
                                shortVerificationId: sdkOptions.id,
                                identifier: deviceData.id,
                                ...deviceData.checks,
                            });
                        });
                    });
            };

            checkDevice();

            fingerprintIntervalRef.current = setInterval(() => {
                checkDevice();
            }, 15000);
        }

        return () => {
            if (fingerprintIntervalRef.current) {
                clearInterval(fingerprintIntervalRef.current);
            }
        };
    }, [uploadFingerprint, sdkOptions, currentStep]);

    const isWidgetLoading = useMemo(() => isLoading || !ready, [isLoading, ready]);

    return (
        <I18nextProvider i18n={i18nInstance}>
            <StyledEngineProvider injectFirst={true}>
                <MuiThemeProvider theme={mainTheme}>
                    <ThemeProvider theme={mainTheme}>
                        <DemoDataContextProvider>
                            <PersonalDataContextProvider>
                                <WidgetContextProvider containerRef={containerRef}>
                                    <DocsVerificationWidgetContainer
                                        id='documents-widget'
                                        ref={containerRef}
                                    >
                                        <ScopedCssBaseline>
                                            <GlobalStyles />
                                            <ToastContainer />
                                            <div>
                                                {isWidgetLoading && (
                                                    <Box
                                                        display='flex'
                                                        justifyContent='center'
                                                        alignItems='center'
                                                        style={{ height }}
                                                    >
                                                        <Loader />
                                                    </Box>
                                                )}
                                                {isError && <VerificationError />}
                                                {!isWidgetLoading && sdkOptions && (
                                                    <DocsVerificationSteps
                                                        onFinish={rest.onFinish}
                                                        getZkProof={rest.getZkProof}
                                                    />
                                                )}
                                            </div>
                                        </ScopedCssBaseline>
                                    </DocsVerificationWidgetContainer>
                                </WidgetContextProvider>
                            </PersonalDataContextProvider>
                        </DemoDataContextProvider>
                    </ThemeProvider>
                </MuiThemeProvider>
            </StyledEngineProvider>
        </I18nextProvider>
    );
};

export const DocsVerificationWidget = (props: DocsVerificationWidgetProps) => {
    const container = document.getElementById(props.elementId);

    if (container) {
        const root = createRoot(container);

        root.render(
            <React.StrictMode>
                <StoreWrapper>
                    <Widget {...props} />
                </StoreWrapper>
            </React.StrictMode>,
        );
    }
};
